// Utilities for computing Tire Performance summary per serial_number.
// All calculations are performed strictly against the history belonging to the same serial_number.

export const toNumber = (val) => {
  if (val === null || val === undefined || val === '') return 0;
  const n = Number(val);
  return Number.isFinite(n) ? n : 0;
};

export const safeDivide = (numerator, denominator) => {
  const n = toNumber(numerator);
  const d = toNumber(denominator);
  if (!d) return 0;
  const result = n / d;
  return Number.isFinite(result) ? result : 0;
};

export const buildTirePerformanceRows = (payload) => {
  const list = Array.isArray(payload) ? payload : payload ? [payload] : [];

  return list.filter(Boolean).map((s) => {
    const unit_price = toNumber(s.unit_price);
    const total_repair_cost = toNumber(s.total_repair_cost);
    const total_cost = unit_price + total_repair_cost;

    const total_km = toNumber(s.total_km);
    const total_hm = toNumber(s.total_hm);

    const sizeDisplay = s.tire_size_code_internal || s.join?.tire_size_code_internal || s.tire_size_code || s.join?.tire_size_code || '-';

    return {
      ...s,
      last_vehicle: s.last_vehicle || '-',
      last_position: s.last_position || '-',
      brand_display: [s.tire_brand_code_internal, s.tire_brand_name].filter(Boolean).join(' - ') || '-',
      tire_size_code_internal: sizeDisplay,
      tire_size_code: sizeDisplay,
      unit_price,
      total_repair_cost,
      total_cost,
      total_km,
      total_hm,
      cpk: safeDivide(total_cost, total_km),
      cph: safeDivide(total_cost, total_hm),
      history: [] // History is now fetched on demand
    };
  });
};

/**
 * Find the position (code & label) of a serial number inside a position payload.
 */
export const findPositionInfo = (posData, sn) => {
  if (!sn || !posData?.axle_info?.info) return null;
  for (const axle of posData.axle_info.info) {
    for (const pos of axle?.positions || []) {
      if (pos?.serial_number === sn) return { code: pos.code || '', label: pos.label || '' };
    }
  }
  return null;
};

export const formatPositionInfo = (posData, sn) => {
  const info = findPositionInfo(posData, sn);
  if (!info) return '-';
  return [info.code, info.label].filter(Boolean).join(' - ') || '-';
};

// Formatting helpers (consistent with id-ID formatting used across the project)
export const formatNumber = (val, maxFractionDigits = 0) =>
  toNumber(val).toLocaleString('id-ID', { maximumFractionDigits: maxFractionDigits });

export const formatCurrency = (val, maxFractionDigits = 0) => `Rp ${formatNumber(val, maxFractionDigits)}`;

export const getPerformanceColumns = (t) => [
  { field: 'serial_number', headerName: t('tireRepairScrap.serialNumberExport', 'Serial Number') },
  { field: 'last_vehicle', headerName: t('tireHistorical.lastVehicle', 'Last Vehicle') },
  { field: 'last_position', headerName: t('detailTireInventory.lastPosition', 'Last Position') },
  { field: 'tire_spec_name', headerName: t('specification.spec_name', 'Spec Name') },
  { field: 'brand_display', headerName: t('tireRepairScrap.brandExport', 'Brand') },
  {
    field: 'tire_size_code_internal',
    headerName: t('tireRepairScrap.sizeExport', 'Size'),
    valueGetter: (row) =>
      row?.tire_size_code_internal || row?.tire_size_code || row?.join?.tire_size_code_internal || row?.join?.tire_size_code || '-'
  },
  { field: 'pattern', headerName: t('specification.pattern', 'Pattern') },
  { field: 'pressure_std', headerName: t('specification.pressureStd', 'Pressure Std (psi)'), type: 'number' },
  { field: 'original_tread_depth', headerName: t('specification.originalTreadDepth', 'Original Tread Depth (mm)'), type: 'number' },
  { field: 'min_tread_depth', headerName: t('tirePerformance.minTreadDepth', 'Min Tread Depth (mm)'), type: 'number' },
  { field: 'unit_price', headerName: t('tireReceiving.unitPrice', 'Unit Price'), type: 'currency' },
  { field: 'init_km_unit', headerName: t('tirePerformance.initKmUnit', 'Init KM Unit'), type: 'number' },
  { field: 'init_hm_unit', headerName: t('tirePerformance.initHmUnit', 'Init HM Unit'), type: 'number' },
  { field: 'total_km', headerName: t('tirePerformance.totalKm', 'Total KM'), type: 'number' },
  { field: 'total_hm', headerName: t('tirePerformance.totalHm', 'Total HM'), type: 'number' },
  { field: 'total_repair_cost', headerName: t('tirePerformance.totalRepairCost', 'Total Repair Cost'), type: 'currency' },
  { field: 'total_cost', headerName: t('tireRepairScrap.totalCost', 'Total Cost'), type: 'currency' },
  { field: 'cpk', headerName: t('tirePerformance.cpk', 'CPK'), type: 'currency2' },
  { field: 'cph', headerName: t('tirePerformance.cph', 'CPH'), type: 'currency2' }
];

export const formatPerformanceCell = (row, col) => {
  const val = col?.valueGetter ? col.valueGetter(row) : row?.[col.field];
  switch (col.type) {
    case 'number':
      return val === null || val === undefined || val === '' ? '-' : formatNumber(val, 2);
    case 'currency':
      return formatCurrency(val);
    case 'currency2':
      return formatCurrency(val, 2);
    default:
      return val === null || val === undefined || val === '' ? '-' : String(val);
  }
};

// Raw values for export (numbers kept numeric for Excel/CSV)
export const getPerformanceExportValue = (row, col) => {
  const val = col?.valueGetter ? col.valueGetter(row) : row?.[col.field];
  if (col.type === 'currency2') return Math.round(toNumber(val) * 100) / 100;
  if (col.type) return toNumber(val);
  return val === null || val === undefined || val === '' ? '-' : String(val);
};
